import { getApiData, listApiData, postApiData } from "@hooks/utils/api";
import { catchSentryError } from "@utils/sentry";
import { getPublicImageUrl } from "@utils/storage";
import { showToast } from "@utils/toast";
import React, { useCallback, useEffect, useState } from "react";
import {
  decodeProject,
  decodeProjectDetail,
  encodeProject,
  encodeProjectDetails
} from "catchment-common/decoders/project";
import { Project, ProjectDetail } from "catchment-common/entities/project";
import { ProfileLineItem } from "catchment-common/interfaces/profile";
import {
  ProjectDetailLineItem,
  ProjectLineItem,
  newProjectToProjectDetail
} from "catchment-common/interfaces/project";

export const useProject = (
  userProfile?: ProfileLineItem,
  projectId?: string,
  archived?: boolean
) => {
  const [selectedProject, setSelectedProject] = useState<ProjectLineItem>();
  const [projects, setProjects] = useState<ProjectDetailLineItem[]>([]);
  const [isProjectLoading, setIsProjectLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const acceptPackage = useCallback(
    async (projectId: string, contractorPackageId: string) => {
      try {
        setIsProjectLoading(true);
        await postApiData("acceptPackage", "contractorPackage", {
          contractorPackageId,
          projectId
        });
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not accept package");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [getPublicImageUrl]
  );

  const getProject = useCallback(
    async (id: string) => {
      try {
        setIsProjectLoading(true);
        const project = (await getApiData("getProject", "project", id))
          .data as Project;
        if (project) {
          const projectLineItem = await decodeProject(
            project,
            getPublicImageUrl
          );
          setSelectedProject(projectLineItem);
        } else {
          setSelectedProject(undefined);
        }
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not fetch project");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [getPublicImageUrl]
  );

  const listProjects = useCallback(
    async (organisationId: string, archived?: boolean) => {
      try {
        setIsProjectLoading(true);

        const projectRes = await listApiData(
          "listProjectDetails",
          "projectDetail",
          organisationId,
          {
            archived: archived === true ? "true" : undefined
          }
        );
        const projectDetailLineItems = await Promise.all(
          projectRes.data.map((p) =>
            decodeProjectDetail(p as ProjectDetail, getPublicImageUrl)
          )
        );
        setProjects(projectDetailLineItems);
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not list project details");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [getPublicImageUrl]
  );

  const createProject = useCallback(
    async (project: ProjectLineItem) => {
      try {
        setIsProjectLoading(true);
        await postApiData("createProject", "project", encodeProject(project));
        setProjects([newProjectToProjectDetail(project), ...projects]);
        showToast("New Project Added");
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not create project");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [projects]
  );

  const updateProject = useCallback(
    async (project: ProjectLineItem) => {
      try {
        setIsProjectLoading(true);
        await postApiData("updateProject", "project", encodeProject(project));
        setSelectedProject(project);
        setProjects(
          projects.map((c) => (c.id === project.id ? { ...c, ...project } : c))
        );
        showToast("Project Updated");
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not update project");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [projects]
  );

  const updateProjectDetail = useCallback(
    async (project: ProjectDetailLineItem) => {
      try {
        setIsProjectLoading(true);
        await postApiData(
          "upsertProjectDetail",
          "projectDetail",
          encodeProjectDetails(project)
        );
        setProjects(
          projects
            .map((c) => (c.id === project.id ? project : c))
            .filter((p) => p.archived === archived)
        );
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not update project detail");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [projects, archived]
  );

  const deleteProject = useCallback(
    async (project: ProjectLineItem) => {
      try {
        setIsProjectLoading(true);
        await postApiData("deleteProject", "project", encodeProject(project));
        setProjects(projects.filter((p) => p.id !== project.id));
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not delete project");
      } finally {
        setIsProjectLoading(false);
      }
    },
    [projects]
  );

  const closeProject = React.useCallback(
    async (project: ProjectDetailLineItem): Promise<void> => {
      const originalProject = { ...project }; // Store original for revert
      const updatedProject = { ...project, isClosed: true };

      // Optimistic update
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? updatedProject : p))
      );

      try {
        await postApiData("updateProjectClosedStatus", "project", {
          isClosed: "true",
          projectId: project.id
        } as any);
        showToast("Project Closed");
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not close project");
        // Revert optimistic update
        setProjects((prev) =>
          prev.map((p) => (p.id === project.id ? originalProject : p))
        );
      }
    },
    []
  );

  const reopenProject = React.useCallback(
    async (project: ProjectDetailLineItem): Promise<void> => {
      const originalProject = { ...project }; // Store original for revert
      const updatedProject = { ...project, isClosed: false };

      // Optimistic update
      setProjects((prev) =>
        prev.map((p) => (p.id === project.id ? updatedProject : p))
      );

      try {
        await postApiData("updateProjectClosedStatus", "project", {
          isClosed: "false",
          projectId: project.id
        } as any);
        showToast("Project Reopened");
      } catch (e: any) {
        catchSentryError(e);
        setError("Could not reopen project");
        // Revert optimistic update
        setProjects((prev) =>
          prev.map((p) => (p.id === project.id ? originalProject : p))
        );
      }
    },
    []
  );

  useEffect(() => {
    if (projectId) {
      getProject(projectId);
    }
    if (userProfile) {
      listProjects(userProfile.organisationId, archived);
    }
  }, [userProfile, projectId, archived]);

  return {
    acceptPackage,
    closeProject,
    createProject,
    deleteProject,
    error,
    getProject,
    isProjectLoading,
    listProjects,
    projects,
    reopenProject,
    selectedProject,
    setSelectedProject,
    updateProject,
    updateProjectDetail
  };
};
